/**
 * The palette's pure helpers.
 *
 * Deliberately free of JSX and of React so `search.test.ts` can pin every rule
 * without a renderer. The score ladder itself is NOT here — it lives in
 * `src/data/search.ts` and is re-exported from `@/data/api`, because two sources
 * feed one ranked list (the server scores accounts/markets/transfers, the client
 * scores actions) and two copies of that table would drift inside a week.
 */

import type { SearchKind } from '@/data/api'

/* ------------------------------------------------------------- platform -- */

/**
 * ⌘ on a Mac, Ctrl everywhere else.
 *
 * Never hardcode ⌘: half the operators of the product being replaced run
 * Windows, and a hint that names a key their keyboard does not have reads as a
 * shortcut that does not exist. `userAgentData` first because `navigator.platform`
 * is deprecated and already frozen to "MacIntel" on Apple Silicon.
 */
export function shortcutHint(): '⌘K' | 'Ctrl K' {
  if (typeof navigator === 'undefined') return 'Ctrl K'
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } }
  const platform = nav.userAgentData?.platform ?? nav.platform ?? ''
  return /mac/i.test(platform) ? '⌘K' : 'Ctrl K'
}

/**
 * Is the event target somewhere a bare "/" is a character the operator meant?
 *
 * ⌘K is safe to steal from anywhere — it is not a text-editing key — but "/" is,
 * so the bare-slash shortcut has to stand down inside any field. Checked by
 * shape rather than by a `closest('input')` walk so a contenteditable inside a
 * custom control is caught too.
 */
export function isTextEntry(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) return false
  const el = target as HTMLElement
  const tag = el.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
  return el.isContentEditable === true || el.closest('[contenteditable="true"]') !== null
}

/* ------------------------------------------------------------ highlight -- */

export interface HighlightPart {
  text: string
  mark: boolean
}

/**
 * Split a title into marked and unmarked runs.
 *
 * Defensive about its input on purpose: `ranges` arrive from the data layer,
 * which computes them against fields that may be concatenated into the title at
 * an offset. A range that is out of order, overlapping or off the end must
 * degrade to plain text — a palette that throws while you type is worse than one
 * that forgets to highlight.
 */
export function highlightParts(title: string, ranges: [number, number][]): HighlightPart[] {
  if (!title) return []
  const clean = ranges
    .map(([start, end]): [number, number] => [
      Math.max(0, Math.min(start, title.length)),
      Math.max(0, Math.min(end, title.length)),
    ])
    .filter(([start, end]) => end > start)
    .sort((a, b) => a[0] - b[0])

  if (clean.length === 0) return [{ text: title, mark: false }]

  // Merge overlaps first: two <mark> elements that touch render as two boxes
  // with a seam down the middle.
  const merged: [number, number][] = []
  for (const range of clean) {
    const last = merged[merged.length - 1]
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1])
    else merged.push([range[0], range[1]])
  }

  const parts: HighlightPart[] = []
  let cursor = 0
  for (const [start, end] of merged) {
    if (start > cursor) parts.push({ text: title.slice(cursor, start), mark: false })
    parts.push({ text: title.slice(start, end), mark: true })
    cursor = end
  }
  if (cursor < title.length) parts.push({ text: title.slice(cursor), mark: false })
  return parts
}

/* --------------------------------------------------------------- groups -- */

/** Tie-break order when two groups have equally-scoring best hits. */
export const GROUP_ORDER: SearchKind[] = ['action', 'account', 'market', 'transfer']

/**
 * Order the four groups by their best hit, descending.
 *
 * There is no separate "top result" section: a hoisted row that also appears in
 * its own group below is a duplicate the eye has to reconcile every time.
 * Score-ordering produces the same outcome — type a username and Agents floats
 * up, type "settle" and Actions does — with one fewer concept on screen.
 */
export function orderGroups<T extends { kind: SearchKind; hits: { score: number }[] }>(
  groups: T[],
): T[] {
  return [...groups].sort((a, b) => {
    const best = (g: T) => g.hits[0]?.score ?? -1
    const delta = best(b) - best(a)
    if (delta !== 0) return delta
    return GROUP_ORDER.indexOf(a.kind) - GROUP_ORDER.indexOf(b.kind)
  })
}

/* -------------------------------------------------------------- see-all -- */

/**
 * Where a capped group's last row goes, and what it says.
 *
 * Each destination is a real list screen that honours `?q=`, so "see all" lands
 * on the same set the palette was showing a slice of rather than on an
 * unfiltered page the operator has to re-narrow by hand.
 */
export const SEE_ALL: Record<SearchKind, { noun: string; to: (text: string) => string } | null> = {
  account: { noun: 'agents & members', to: () => '/aggregate/accounts' },
  market: { noun: 'in the live book', to: (t) => `/exposure?q=${encodeURIComponent(t)}` },
  // Transfers live on each member statement — no global list screen remains.
  transfer: null,
  // Actions are never capped — there are at most a dozen and no list page to
  // send anyone to.
  action: null,
}

export function seeAllLabel(kind: SearchKind, total: number): string | null {
  const spec = SEE_ALL[kind]
  return spec ? `See all ${total} ${spec.noun} →` : null
}

/* --------------------------------------------------------------- status -- */

/**
 * The sr-only live announcement.
 *
 * Counts the CAPPED hits, not the totals: it describes what is on screen and
 * reachable with ↓, which is the only thing a cursor-driven announcement can
 * usefully mean. The "of 41" honesty lives in the group head, where it is
 * attached to the group it qualifies.
 */
export function statusLine(groups: { label: string; count: number }[]): string {
  const shown = groups.filter((g) => g.count > 0)
  const total = shown.reduce((sum, g) => sum + g.count, 0)
  if (total === 0) return 'No results'
  const parts = shown.map((g) => `${g.count} in ${g.label.toLowerCase()}`)
  return `${total} result${total === 1 ? '' : 's'} — ${parts.join(', ')}`
}
